import { useEffect, useState } from 'react';

/**
 * Returns true when the user has requested reduced motion
 * (prefers-reduced-motion: reduce). Updates live if the OS setting changes.
 *
 * Every motion primitive in src/components/motion reads this so that
 * animation is a progressive enhancement, never a barrier (WCAG 2.3.3).
 * SSR/first-paint safe: defaults to `false` on the server and syncs on mount.
 */
export function useReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setReduced(mql.matches);
    onChange();
    // addEventListener is the modern API; older Safari uses addListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return reduced;
}

export default useReducedMotion;
